import React from "react";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
  Plugin as ChartPlugin,
} from "chart.js";
import { Box, useColorModeValue } from "@chakra-ui/react";

ChartJS.register(ArcElement, Tooltip, Legend);

/* ─────────────── CUSTOM VALUE-ONLY LABELS PLUGIN ──────────────── */
const valueOnlyLabels: ChartPlugin<"pie"> = {
  id: "valueOnlyLabels",
  afterDraw(chart) {
    const { ctx } = chart;
    const dataset = chart.data.datasets[0];

    // bail if we're in "all-zero" (grey) mode
    if ((dataset.data as number[]).every((v) => v === 0)) return;

    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((el, i) => {
      const arc = el as unknown as ArcElement;
      const value = dataset.data[i] as number;
      if (value === 0) return;

      // ─── LABEL TEXT - ONLY VALUE ───────────────────────────────────────
      const label = `${value}`;

      // ─── LEADER LINE GEOMETRY ─────────────────────────────────────────────
      const mid = (arc.startAngle + arc.endAngle) / 2;
      const r = arc.outerRadius;
      const cx = arc.x;
      const cy = arc.y;
      const x1 = cx + Math.cos(mid) * r;
      const y1 = cy + Math.sin(mid) * r;
      const x2 = cx + Math.cos(mid) * (r + 15);
      const y2 = cy + Math.sin(mid) * (r + 15);

      // Draw the line
      ctx.strokeStyle = "#9ca3af";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // Draw the text - only value (high-contrast on light bg)
      ctx.font = "600 11px sans-serif";
      ctx.fillStyle = "#111827"; // gray-900
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 3;
      ctx.textBaseline = "middle";
      ctx.textAlign =
        mid > Math.PI / 2 && mid < (3 * Math.PI) / 2 ? "right" : "left";
      const tx = x2 + (ctx.textAlign === "left" ? 4 : -4);
      const ty = y2;
      ctx.strokeText(label, tx, ty);
      ctx.fillText(label, tx, ty);
    });
  },
};

ChartJS.register(valueOnlyLabels);
/* ───────────────────────────────────────────────────────────── */

interface TaskOverviewChartProps {
  delayedTasks: number;
  notStartedTasks: number;
  completedTasks: number;
  inProgressTasks: number;
}

const TaskOverviewChart: React.FC<TaskOverviewChartProps> = ({
  delayedTasks,
  notStartedTasks,
  completedTasks,
  inProgressTasks,
}) => {
  const axisText = useColorModeValue("#111827", "#E5E7EB");
  const legendMuted = useColorModeValue("#374151", "#C3C9D3");
  /* ─── PREPARE DATA ─────────────────────────────────────────────────── */
  const taskData = [
    delayedTasks,
    notStartedTasks,
    completedTasks,
    inProgressTasks,
  ];
  const total = taskData.reduce((s, n) => s + n, 0);

  const data = total
    ? {
        labels: ["Delayed", "Not Started", "Completed", "In Progress"],
        datasets: [
          {
            data: taskData,
            // Order follows taskData: [Delayed, Not Started, Completed, In Progress]
            // Delayed emphasized in red; Not Started in blue
            backgroundColor: ["#EF4444", "#2563EB", "#16A34A", "#EAB308"],
            borderColor: "#ffffff",
            borderWidth: 2,
          },
        ],
      }
    : {
        labels: ["no tasks"],
        datasets: [
          {
            data: [1],
            backgroundColor: ["#E5E7EB"], // slate-200 grey
            borderColor: "#ffffff",
            borderWidth: 2,
          },
        ],
      };

  /* ─── CHART OPTIONS ───────────────────────────────────────────────── */
  const options: ChartOptions<"pie"> = {
    responsive: true,
    maintainAspectRatio: false,
  layout: { padding: { top: 22, right: 22, bottom: 22, left: 22 } },
    plugins: {
      legend: {
        display: false, // We'll create custom legend below
      },
      tooltip: {
        enabled: total !== 0,
        callbacks: {
          label: (ctx: TooltipItem<"pie">) => {
            const label = ctx.label ?? "";
            const value = ctx.parsed as number;
            const pct = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${pct}%)`;
          },
        },
      },
    },
  };

  /* ─── RENDER ───────────────────────────────────────────────────────── */
  return (
    <Box className="flex w-full h-full items-center justify-center">
      {/* LEFT: PIE CHART */}
      <Box position="relative" width="220px" height="220px" mr={6}>
        <Pie data={data} options={options} />
      </Box>

      {/* RIGHT: VERTICAL LEGEND WITH COUNTS */}
      <Box className="flex flex-col gap-3">
        {total > 0 ? (
          data.labels.map((label, index) => {
            const value = taskData[index];
            const color = data.datasets[0].backgroundColor[index];
            return (
              <div key={label} className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm font-medium" style={{ color: legendMuted }}>
                  {label}
                </span>
                <span className="text-sm font-bold" style={{ color: axisText }}>
                  {value}
                </span>
              </div>
            );
          })
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#E5E7EB" }} />
            <span className="text-sm" style={{ color: legendMuted }}>No Tasks</span>
            <span className="text-sm font-bold" style={{ color: axisText }}>0</span>
          </div>
        )}
      </Box>
    </Box>
  );
};

export default TaskOverviewChart;
